import React, { useState, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import { usePerDiemForm } from '../hook/userPerDiemLodgingForm.mjs';
import { AuthContext } from '../../authProvider.mjs';
import { useParams } from 'react-router-dom';
import { PerDiemService } from '../service/perdiemService.mjs';
import PropTypes from 'prop-types';
import { ClaimStatusBanner } from '../../components/claimStatusBanner.mjs';
import { AdminFeedbackSection } from '../../components/adminFeedbackBanner.mjs';
import ClaimQuestionsBanner from '../../clarifications/components/ClaimQuestionsBanner.mjs';
import PdfSupportingViewer from '../../components/pdfSupportingViewer.mjs';
import InfectedFileNotice from '../../components/InfectedFileNotice.mjs';
import { isInfected } from '../../utils/scanStatus.mjs';
import ReactDOM from 'react-dom';
import seedlogo from '../../assets/seedlogo.jpg';
import { parseLocalDate, formatLocalDate, startOfDay, isCompleteDateString, inclusiveDayCount } from '../../utils/dateOnly.mjs';
import DateInput from '../../components/DateInput.mjs';
import ClaimPtaeAllocation from '../../components/ClaimPtaeAllocation.mjs';
import ClaimHistoryPanel from '../../components/ClaimHistoryPanel.mjs';
import { exportPerDiemClaimToExcel } from '../../utils/claimExcel.mjs';
import { useDialog } from '../../components/dialog/DialogProvider.mjs';
import PageShell from '../../components/PageShell.mjs';




// Utility functions
const isFutureDate = (date) => {
  // startOfDay handles both Date objects and YYYY-MM-DD strings in LOCAL time,
  // so this can't disagree with the range checks that use the same basis.
  const today = startOfDay(new Date());
  const checkDate = startOfDay(date);
  if (!checkDate) return false;
  return checkDate > today;
};

const isDateWithinTravelRange = (date, startDate, endDate) => {
  if (!startDate || !endDate || !date) return false;
  // startOfDay parses date-only strings in LOCAL time, so a range means the
  // same calendar days regardless of the user's timezone.
  const checkDate = startOfDay(date);
  const start = startOfDay(startDate);
  const end = startOfDay(endDate);
  if (!checkDate || !start || !end) return false;

  return checkDate >= start && checkDate <= end;
};

// Rate components a traveller can waive for the whole claim (e.g. meals
// provided by the host). Quick Fill honours these while generating rows.
const EXCLUSIONS = [
  { key: 'lodging',   label: 'No Lodging' },
  { key: 'breakfast', label: 'No Breakfast' },
  { key: 'lunch',     label: 'No Lunch' },
  { key: 'dinner',    label: 'No Dinner' },
];

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "Apr 1 - Jan 31". A season window can wrap the year end, which reads fine
// here because the start is simply printed before the end.
const formatSeasonWindow = (s) => {
  if (!s || !s.startMonth || !s.endMonth) return '';
  return `${MONTH_ABBR[s.startMonth - 1]} ${s.startDay} - ${MONTH_ABBR[s.endMonth - 1]} ${s.endDay}`;
};

const isDuplicateDate = (date, lineItems, excludeId = null) => {
  // Compare LOCAL calendar days. This used toISOString(), i.e. the UTC day -
  // so east of UTC a local midnight fell on the previous UTC date, producing
  // "a line item for this date already exists" for dates that didn't clash
  // (and missing genuine duplicates).
  const target = startOfDay(date);
  if (!target) return false;
  return lineItems.some(item => {
    if (item.id === excludeId) return false;
    const day = startOfDay(item.date);
    return day && day.getTime() === target.getTime();
  });
};

// Sub-components
const TravelerInfoSection = ({
  user, lastName, setLastName, firstName, setFirstName, position, setPosition,
  businessPurpose, setBusinessPurpose, startDate, setStartDate, endDate, setEndDate,
  isReadOnly, formatDateForInput, parseInputDate: _parseInputDate, claimData, adminMode,
  onBehalfOf = null,
}) => {
  // Filing for someone: their details, from their account, not editable here.
  // Otherwise the admin's own name would sit on a claim that pays someone else.
  const isViewingOthersClaim = !!onBehalfOf
    || (adminMode && claimData?.user && claimData.user.id !== user?.id);

  // Fixed: Always prioritize claim data when it exists, regardless of admin mode
  const displayLastName = onBehalfOf?.lastName || claimData?.user?.lastName ||
                          claimData?.lastName ||
                          (adminMode && claimData ? '' : (user?.lastName || lastName || ''));

  const displayFirstName = onBehalfOf?.firstName || claimData?.user?.firstName ||
                           claimData?.firstName ||
                           (adminMode && claimData ? '' : (user?.firstName || firstName || ''));

  const displayPosition = onBehalfOf?.position || claimData?.user?.position ||
                          claimData?.position ||
                          (adminMode && claimData ? '' : (user?.position || position || ''));

  // Identity - name and position - comes from the claimant's account, so it is
  // locked whenever the claim is someone else's.
  const fieldsReadOnly = isReadOnly || isViewingOthersClaim;
  // The trip itself is the substance of the claim. Filing for someone, the
  // admin is the one who has to write it, and an admin correcting someone's
  // existing claim may reword it too - the server already accepts it on the
  // admin update and records the edit. Other reviewers (auditor, directors)
  // opening someone's claim in admin mode still see it locked.
  const purposeReadOnly = isReadOnly
    || (!onBehalfOf && adminMode && claimData?.user && claimData.user.id !== user?.id
        && user?.role !== 'admin');
  const purposeReadOnlyClass = purposeReadOnly ? "bg-gray-100 cursor-not-allowed" : "";

  const inputBaseClass = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent";
  const readOnlyClass = fieldsReadOnly ? "bg-gray-100 cursor-not-allowed" : "";

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 max-sm:p-5 mb-6 max-sm:mb-4">
      <div className="flex flex-wrap items-start gap-2 sm:gap-4 mb-4">
        <div className="text-sm font-medium text-gray-700 w-full sm:w-auto sm:min-w-[140px] sm:pt-2">Traveler Name:</div>
        <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-3 sm:gap-4 flex-1 w-full sm:w-auto">
          <div className="flex flex-col sm:min-w-[180px]">
            <label className="text-xs text-gray-500 mb-1">Last/Surname</label>
            <input
              type="text"
              value={displayLastName}
              onChange={(e) => setLastName(e.target.value)}
              readOnly={fieldsReadOnly}
              className={`${inputBaseClass} ${readOnlyClass}`}
              aria-label="Last name"
              placeholder={adminMode ? "Traveler's last name" : "Your last name"}
            />
          </div>
          <div className="flex flex-col sm:min-w-[180px]">
            <label className="text-xs text-gray-500 mb-1">First/Given</label>
            <input
              type="text"
              value={displayFirstName}
              onChange={(e) => setFirstName(e.target.value)}
              readOnly={fieldsReadOnly}
              className={`${inputBaseClass} ${readOnlyClass}`}
              aria-label="First name"
              placeholder={adminMode ? "Traveler's first name" : "Your first name"}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-4 mb-4">
        <div className="text-sm font-medium text-gray-700 sm:min-w-[140px]">Position:</div>
        <input
          type="text"
          className={`flex-1 ${inputBaseClass} ${readOnlyClass}`}
          value={displayPosition}
          onChange={(e) => setPosition(e.target.value)}
          readOnly={fieldsReadOnly}
          aria-label="Position"
          placeholder={adminMode ? "Traveler's position" : "Your position"}
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start gap-1 sm:gap-4 mb-4">
        <div className="text-sm font-medium text-gray-700 sm:min-w-[140px] sm:pt-2">Business Purpose:</div>
        <textarea
          className={`flex-1 min-h-[80px] ${inputBaseClass} ${purposeReadOnlyClass}`}
          value={businessPurpose}
          onChange={(e) => setBusinessPurpose(e.target.value)}
          readOnly={purposeReadOnly}
          aria-label="Business purpose"
          placeholder={adminMode ? "Business purpose of travel" : "Your business purpose"}
        />
      </div>

      {fieldsReadOnly && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-700">
            {onBehalfOf
              ? `Name and position come from ${onBehalfOf.firstName}'s account and cannot be changed here.`
              : isViewingOthersClaim
                ? "Personal details cannot be modified when viewing another user's claim."
                : "You are viewing this claim in read-only mode. Click \"Enable Editing\" at the top of the page to make changes."
            }
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-start gap-1 sm:gap-4 mb-4">
        <div className="text-sm font-medium text-gray-700 w-full sm:w-auto sm:min-w-[140px] sm:pt-2">Dates of Travel:</div>
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:flex items-center gap-2 w-full sm:w-auto">
          <DateInput
            value={startDate}
            onChange={setStartDate}
            className={`${inputBaseClass} ${isReadOnly ? "bg-gray-100 cursor-not-allowed" : ""}`}
            readOnly={isReadOnly}
            max={formatDateForInput(new Date())}
            aria-label="Start date"
          />
          <span className="text-gray-500">to</span>
          <DateInput
            value={endDate}
            onChange={setEndDate}
            className={`${inputBaseClass} ${isReadOnly ? "bg-gray-100 cursor-not-allowed" : ""}`}
            readOnly={isReadOnly}
            min={formatDateForInput(startDate)}
            max={formatDateForInput(new Date())}
            aria-label="End date"
          />
        </div>
        <div className="w-full mt-2 text-sm text-gray-500">
          Dates here should reflect first and last dates of entire trip, not just range claimed hereon.
          This permits confirmation of reduced rates (if applicable) and 75% for first and last days.
          <strong className="block mt-1"> Note: Claims cannot be filed for future travel dates.</strong>
        </div>
      </div>

      <div className="text-sm text-gray-600">
        Use <a href="https://allowances.state.gov/web920/per_diem.asp" target="_blank" rel="noopener noreferrer" className="text-stanford-red hover:text-stanford-light-red underline break-all">https://allowances.state.gov/web920/per_diem.asp</a> for verifying per diem rate.
      </div>
    </div>
  );
};


const QuickFillSection = ({
  quickFillStartDate, setQuickFillStartDate, quickFillEndDate, setQuickFillEndDate,
  quickFillCity, quickFillCountry, activeSearchId, citySearch, countrySearch,
  handleQuickFillCityInputChange, handleQuickFillCountryInputChange,
  positionDropdown, setActiveSearchId, setCitySearch, setCountrySearch,
  setQuickFillCity: _setQuickFillCity, setQuickFillCountry: _setQuickFillCountry, handleQuickFill,
  exclusions = {}, onExclusionChange,
  quickFillSegments = [], onAddQuickFillSegment, onRemoveQuickFillSegment,
  isReadOnly, claimStatus, startDate, endDate, formatDateForInput, isQuickFillLoading
}) => {
  if (claimStatus === 'approved') {
    return null;
  }

  const getMaxDate = () => {
    const today = formatDateForInput(new Date());
    const travelEnd = formatDateForInput(endDate);

    if (!travelEnd) return today;
    return today < travelEnd ? today : travelEnd;
  };

  const getMinDate = () => {
    return formatDateForInput(startDate);
  };

  const inputBaseClass = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent";
  const disabledClass = isReadOnly || isQuickFillLoading ? "bg-gray-100 cursor-not-allowed" : "";

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 max-sm:p-5 mb-6 max-sm:mb-4 relative">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Quick Fill</h2>
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-4 mb-4">
        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-700 mb-1">Date Range:</label>
          <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:flex items-center gap-2">
            <input
              type="date"
              value={quickFillStartDate}
              onChange={(e) => setQuickFillStartDate(e.target.value)}
              className={`${inputBaseClass} ${disabledClass}`}
              readOnly={isReadOnly || isQuickFillLoading}
              min={getMinDate()}
              max={getMaxDate()}
              aria-label="Quick fill start date"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={quickFillEndDate}
              onChange={(e) => setQuickFillEndDate(e.target.value)}
              className={`${inputBaseClass} ${disabledClass}`}
              readOnly={isReadOnly || isQuickFillLoading}
              min={quickFillStartDate || getMinDate()}
              max={getMaxDate()}
              aria-label="Quick fill end date"
            />
          </div>
        </div>

        <div className="flex flex-col sm:min-w-[180px]">
          <label className="text-sm font-medium text-gray-700 mb-1">Country:</label>
          <input
            id="quickFillCountryInput"
            type="text"
            value={activeSearchId === 'quickFillCountry' ? countrySearch : quickFillCountry}
            onChange={(e) => !isQuickFillLoading && handleQuickFillCountryInputChange(e.target.value)}
            onFocus={() => {
              if (!isQuickFillLoading) {
                setActiveSearchId('quickFillCountry');
                setCountrySearch(quickFillCountry);
                setTimeout(() => positionDropdown('quickFillCountry'), 50);
              }
            }}
            onBlur={() => {
              setTimeout(() => {
                if (activeSearchId === 'quickFillCountry') {
                  setActiveSearchId(null);
                  setCountrySearch('');
                }
              }, 200);
            }}
            placeholder="Search for country"
            readOnly={isReadOnly || isQuickFillLoading}
            className={`${inputBaseClass} ${disabledClass}`}
            aria-label="Quick fill country search"
          />
        </div>

        <div className="flex flex-col sm:min-w-[180px]">
          <label className="text-sm font-medium text-gray-700 mb-1">City:</label>
          <input
            id="quickFillCityInput"
            type="text"
            value={activeSearchId === 'quickFillCity' ? citySearch : quickFillCity}
            onChange={(e) => !isQuickFillLoading && handleQuickFillCityInputChange(e.target.value)}
            onFocus={() => {
              if (!isQuickFillLoading) {
                setActiveSearchId('quickFillCity');
                setCitySearch(quickFillCity);
                setTimeout(() => positionDropdown('quickFillCity'), 50);
              }
            }}
            onBlur={() => {
              setTimeout(() => {
                if (activeSearchId === 'quickFillCity') {
                  setActiveSearchId(null);
                  setCitySearch('');
                }
              }, 200);
            }}
            placeholder="Search for city"
            readOnly={isReadOnly || isQuickFillLoading}
            className={`${inputBaseClass} ${disabledClass}`}
            aria-label="Quick fill city search"
          />
        </div>

        {/* + button: stack this city so several cities are fetched at once. */}
        <button
          type="button"
          onClick={onAddQuickFillSegment}
          className="flex items-center justify-center gap-1 min-h-[44px] px-3 py-2 border border-stanford-red text-stanford-red rounded-lg hover:bg-stanford-red hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isReadOnly || isQuickFillLoading || !quickFillStartDate || !quickFillEndDate || !quickFillCountry || !quickFillCity}
          aria-label="Add another city to quick fill"
          title="Add another city (visited multiple cities)"
        >
          <span className="text-lg leading-none">+</span>
          <span className="text-sm">Add City</span>
        </button>
      </div>

      {/* Stacked cities awaiting a single Quick Fill fetch. */}
      {quickFillSegments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {quickFillSegments.map(seg => (
            <span
              key={seg.id}
              className="inline-flex items-center gap-2 px-3 py-1 bg-stanford-red/10 text-stanford-red rounded-full text-sm"
            >
              <span>{seg.city}, {seg.country} · {seg.startDate} → {seg.endDate}</span>
              {!isReadOnly && !isQuickFillLoading && (
                <button
                  type="button"
                  onClick={() => onRemoveQuickFillSegment(seg.id)}
                  className="text-stanford-red hover:text-red-800 font-bold leading-none"
                  aria-label={`Remove ${seg.city}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <p className="text-sm text-gray-500 mb-4">
        Quick fill dates must be within your travel dates and cannot be in the future.
        Visited more than one city? Add each with <span className="font-medium">+ Add City</span>, then Quick Fill fetches them all at once.
      </p>

      {/* Exclusions and Quick Fill share one row, Quick Fill last on the right.
          The exclusions are settings Quick Fill honours while generating rows -
          they no longer have to be applied after the lines already exist. */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-x-4 gap-y-1 sm:gap-4">
        {EXCLUSIONS.map(({ key, label }) => (
          <label key={key} className="flex items-center gap-2 min-h-[44px] sm:min-h-0 text-sm text-gray-700 cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={!!exclusions[key]}
              onChange={(e) => !isQuickFillLoading && onExclusionChange(key, e.target.checked)}
              disabled={isReadOnly || isQuickFillLoading}
              className="w-4 h-4 text-stanford-red border-gray-300 rounded focus:ring-stanford-red"
              aria-label={`${label} for all items`}
            />
            {label}
          </label>
        ))}

        <button
          onClick={handleQuickFill}
          className={`col-span-2 sm:ml-auto min-h-[44px] justify-center px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
          disabled={isReadOnly || isQuickFillLoading || (quickFillSegments.length === 0 && (!quickFillCountry || !quickFillCity))}
          aria-label="Apply quick fill"
        >
          {isQuickFillLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Processing...</span>
            </>
          ) : (
            quickFillSegments.length > 0 ? `Quick Fill (${quickFillSegments.length + (quickFillCountry && quickFillCity ? 1 : 0)} cities)` : 'Quick Fill'
          )}
        </button>

      </div>

      {isQuickFillLoading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-xl z-10">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 border-4 border-stanford-red border-t-transparent rounded-full animate-spin"></div>
            <div className="text-center">
              <h4 className="font-semibold text-gray-800">Fetching Per Diem Rates...</h4>
              <p className="text-sm text-gray-600">Please wait while we retrieve the rates for your selected dates and location.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MealCell = ({ value, originalRate, itemId, mealType, updateLineItem, isReadOnly }) => {
  const isRemoved = value === 0 && originalRate > 0;

  const handleToggle = () => {
    if (isRemoved) {
      // Restore to original rate
      updateLineItem(itemId, mealType, originalRate || 0);
    } else {
      // Remove (set to 0)
      updateLineItem(itemId, mealType, 0);
    }
  };

  return (
    <div className="flex items-center gap-2 lg:gap-1">
      <input
        type="number"
        inputMode="numeric"
        value={value === '' || value === null || value === undefined ? 0 : value}
        onChange={(e) => {
          const input = parseFloat(e.target.value);
          const max = originalRate ?? Infinity;
          if (!isNaN(input)) {
            const finalValue = Math.min(input, max);
            updateLineItem(itemId, mealType, finalValue);
          }
        }}
        min={0}
        readOnly={isReadOnly}
        className={`w-full min-w-0 lg:w-14 min-h-[44px] lg:min-h-0 px-2 lg:px-1 py-1 text-sm lg:text-xs border border-gray-300 rounded-lg lg:rounded text-center ${isReadOnly ? "bg-gray-100 cursor-not-allowed" : ""} ${isRemoved ? "bg-red-50 text-red-400" : ""}`}
        aria-label={`${mealType} amount`}
      />
      {!isReadOnly && originalRate > 0 && (
        <button
          type="button"
          onClick={handleToggle}
          className={`shrink-0 w-9 h-9 lg:w-auto lg:h-auto inline-flex items-center justify-center rounded-lg lg:rounded-none text-sm ${isRemoved ? "text-green-600 hover:text-green-800" : "text-red-500 hover:text-red-700"}`}
          title={isRemoved ? `Restore ${mealType}` : `Remove ${mealType}`}
          aria-label={isRemoved ? `Restore ${mealType}` : `Remove ${mealType}`}
        >
          {isRemoved ? "↺" : "✕"}
        </button>
      )}
    </div>
  );
};

const ExpenseTableRow = ({
  item, updateLineItem, deleteLineItem, restoreLineItem: _restoreLineItem, apply75Percent,
  appliedRates, activeSearchId, citySearch, handleCityInputChange, handleCityInputFocus,
  handleKeyDown, formatDateForInput, parseInputDate, inputRefs, adminMode: _adminMode, lineItems,
  isReadOnly, claimStatus, startDate, endDate, isSelected, onToggleSelect
}) => {
  // Custom in-app dialogs replacing window.confirm/window.alert. `alert` is
  // named to shadow the global deliberately: every existing alert(...) call in
  // this file then routes through the styled modal with no other change.
  const { confirm, alert } = useDialog();
  const hasFeedback = claimStatus === 'returned' && item.adminComments && item.adminComments.trim() !== '';
  // Flag rows sharing a calendar day so duplicates are visible immediately,
  // rather than only surfacing when the claim is submitted.
  const isDuplicateRow = isDuplicateDate(item.date, lineItems || [], item.id);
  const isOutsideTrip = !!(item.date && startDate && endDate
    && !isDateWithinTravelRange(item.date, startDate, endDate));

  const handleDateChange = (newDate) => {
    // While a date is partially typed the browser reports an empty value.
    // Bail out early so the field isn't reset before the 4-digit year is
    // finished - and so the validation alerts below don't fire mid-keystroke.
    if (!isCompleteDateString(newDate)) return;

    const parsedDate = parseInputDate(newDate);

    if (isFutureDate(parsedDate)) { alert('Cannot set expense date in the future.');
      return;
    }

    if (!isDateWithinTravelRange(parsedDate, startDate, endDate)) {
      alert('Expense date must be within your travel dates.');
      return;
    }

    if (isDuplicateDate(parsedDate, lineItems, item.id)) {
      alert('A line item for this date already exists.');
      return;
    }

    updateLineItem(item.id, 'date', parsedDate);
  };

  const getMaxDate = () => {
    const today = formatDateForInput(new Date());
    const travelEnd = formatDateForInput(endDate);

    if (!travelEnd) return today;
    return today < travelEnd ? today : travelEnd;
  };

  const getMinDate = () => {
    return formatDateForInput(startDate);
  };

  const isRowReadOnly = isReadOnly || claimStatus === 'approved';
  const inputClass = `w-full min-h-[44px] lg:min-h-0 px-3 lg:px-2 py-2 lg:py-1 text-sm border border-gray-300 rounded-lg lg:rounded ${isRowReadOnly ? "bg-gray-100 cursor-not-allowed" : ""}`;

  return (
    <React.Fragment key={item.id}>
      <div
        className={`line-item-card grid grid-cols-4 ${claimStatus !== 'approved' ? 'lg:grid-cols-[36px_120px_minmax(140px,1fr)_80px_70px_70px_70px_80px_70px_70px_90px_minmax(100px,1fr)_90px]' : 'lg:grid-cols-[36px_120px_minmax(160px,1fr)_80px_70px_70px_70px_80px_70px_70px_90px_minmax(120px,1fr)]'} gap-3 lg:gap-2 p-4 lg:p-2 border-b border-gray-200 items-start lg:items-center text-sm lg:min-w-[1100px] ${hasFeedback ? 'border-l-4 border-l-orange-400 bg-orange-50' : ''} ${isDuplicateRow ? 'border-l-4 border-l-amber-500 bg-amber-50' : ''} ${isOutsideTrip ? 'border-l-4 border-l-red-500 bg-red-50' : ''} ${claimStatus === 'approved' ? 'bg-green-50' : 'hover:bg-gray-50'}`}
        title={hasFeedback ? `Admin Feedback: ${item.adminComments}` : ''}
      >
        <label className="col-span-4 lg:col-span-1 flex items-center lg:justify-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!isSelected}
            onChange={() => onToggleSelect(item.id)}
            disabled={isRowReadOnly}
            className="w-5 h-5 lg:w-4 lg:h-4 accent-stanford-red border-gray-300 rounded focus:ring-stanford-red"
            aria-label="Select this line"
          />
          <span className="lg:hidden text-xs font-medium text-gray-500" aria-hidden="true">Select</span>
        </label>
        {/* Phones have no hover, so the row's feedback tooltip never shows
            there - spell it out in the card instead. */}
        {hasFeedback && (
          <p className="col-span-4 lg:hidden text-sm text-orange-800 bg-orange-100 rounded-lg px-3 py-2 break-words">
            <span className="font-semibold">Admin Feedback:</span> {item.adminComments}
          </p>
        )}
        <div className="col-span-2 lg:col-span-1" data-label="Date">
          <DateInput
            value={item.date}
            onChange={(d) => handleDateChange(formatLocalDate(d))}
            className={`${inputClass} text-xs`}
            readOnly={isRowReadOnly}
            min={getMinDate()}
            max={getMaxDate()}
            aria-label="Expense date"
          />
          {isOutsideTrip && (
            <span
              className="block mt-0.5 text-[11px] font-medium text-red-700"
              title="This day is not within the travel dates"
            >
              Outside travel dates
            </span>
          )}
          {isDuplicateRow && (
            <span
              className="block mt-0.5 text-[11px] font-medium text-amber-700"
              title="Another line already covers this day"
            >
Duplicate day
            </span>
          )}
        </div>

        <div className="col-span-4 lg:col-span-1 lg:order-none order-1" data-label="City, Country" data-required>
          <div className="relative">
            <input
              ref={el => inputRefs.current[item.id] = el}
              type="text"
              value={activeSearchId === item.id ? citySearch : (item.city || '')}
              onChange={(e) => handleCityInputChange(item.id, e.target.value)}
              onFocus={() => !isRowReadOnly && handleCityInputFocus(item.id)}
              onKeyDown={(e) => {
                if ((e.key === 'Backspace' || e.key === 'Delete') && citySearch === '') {
                  updateLineItem(item.id, 'city', '');
                  updateLineItem(item.id, 'country', '');
                }
                handleKeyDown(e, item.id);
              }}
              placeholder="Search for city"
              readOnly={isRowReadOnly}
              className={`${inputClass} ${hasFeedback ? "border-orange-400" : ""}`}
              aria-label="City search"
            />
            {hasFeedback && (
              <div
                className="absolute right-2 top-1/2 -translate-y-1/2 text-yellow-500 cursor-help"
                title={`Admin Feedback: ${item.adminComments}`}
              >
                              </div>
            )}
          </div>
        </div>

        <div className="col-span-2 lg:col-span-1 lg:text-center font-medium text-gray-700 text-sm lg:text-xs" data-label="State Dept M&IE" aria-label="Published M&IE rate">
          {item.stateDeptMIE}
          {/* Alaska / Hawaii / US territories: the Department of Defense sets
              these and publishes no machine-readable file, so there is no rate to
              look up. Say so, or the zeros look like a real $0 rate. */}
          {item.manualEntryRequired && (
            <span
              className="block mt-0.5 text-[10px] font-normal text-amber-700 leading-tight"
              title={item.rateNotice || 'Enter the lodging and M&IE rates for this location manually.'}
            >
              Enter manually (DoD rate) ⓘ
            </span>
          )}
          {/* Locations such as Mumbai publish two seasonal rates. Naming the one
              the date resolved to makes the figure checkable instead of magic. */}
          {item.rateSeason && item.rateSeason.count > 1 && (
            <span
              className="block mt-0.5 text-[10px] font-normal text-blue-700 leading-tight"
              title={
                `This location publishes ${item.rateSeason.count} seasonal rates` +
                (item.rateMonth ? ` (${item.rateMonth} data)` : '') + ':\n' +
                (item.rateSeason.options || [])
                  .map(o =>
                    `${o.code}: ${formatSeasonWindow(o)} - M&IE ${o.mie}, lodging ${o.lodging}` +
                    (o.code === item.rateSeason.code ? '   ← applied to this date' : '')
                  )
                  .join('\n')
              }
            >
              {item.rateSeason.code} · {formatSeasonWindow(item.rateSeason)}
            </span>
          )}
        </div>

        <div className="col-span-2 lg:col-span-1 order-2 lg:order-none" data-label="Breakfast">
          <MealCell
            value={item.breakfast}
            originalRate={item.originalRates?.breakfast}
            itemId={item.id}
            mealType="breakfast"
            updateLineItem={updateLineItem}
            isReadOnly={isRowReadOnly}
          />
        </div>

        <div className="col-span-2 lg:col-span-1 order-2 lg:order-none" data-label="Lunch">
          <MealCell
            value={item.lunch}
            originalRate={item.originalRates?.lunch}
            itemId={item.id}
            mealType="lunch"
            updateLineItem={updateLineItem}
            isReadOnly={isRowReadOnly}
          />
        </div>

        <div className="col-span-2 lg:col-span-1 order-2 lg:order-none" data-label="Dinner">
          <MealCell
            value={item.dinner}
            originalRate={item.originalRates?.dinner}
            itemId={item.id}
            mealType="dinner"
            updateLineItem={updateLineItem}
            isReadOnly={isRowReadOnly}
          />
        </div>

        <div className="col-span-2 lg:col-span-1 order-2 lg:order-none" data-label="Incidentals">
          <MealCell
            value={item.incidentals}
            originalRate={item.originalRates?.incidentals}
            itemId={item.id}
            mealType="incidentals"
            updateLineItem={updateLineItem}
            isReadOnly={isRowReadOnly}
          />
        </div>

        <div className="col-span-2 lg:col-span-1 order-2 lg:order-none lg:text-center font-medium text-gray-700 text-sm lg:text-xs tabular-nums" data-label="Total M&IE" aria-label="Total M&IE">{Number(item.totalMIE)}</div>

        <div className="col-span-2 lg:col-span-1 order-2 lg:order-none" data-label="Lodging">
          {(() => {
            const lodgingValue = item.lodgingCost === '' || item.lodgingCost === null || item.lodgingCost === undefined ? 0 : item.lodgingCost;
            const originalLodging = item.originalRates?.lodgingCost || 0;
            const isLodgingRemoved = lodgingValue === 0 && originalLodging > 0;

            return (
              <div className="flex items-center gap-2 lg:gap-1">
                <input
                  type="number"
                  inputMode="numeric"
                  value={lodgingValue}
                  onChange={(e) => {
                    const input = parseFloat(e.target.value);
                    if (!isNaN(input)) {
                      updateLineItem(item.id, 'lodgingCost', input);
                    }
                  }}
                  min={0}
                  readOnly={isRowReadOnly}
                  className={`w-full min-w-0 lg:w-14 min-h-[44px] lg:min-h-0 px-2 lg:px-1 py-1 text-sm lg:text-xs border border-gray-300 rounded-lg lg:rounded text-center ${isRowReadOnly ? "bg-gray-100 cursor-not-allowed" : ""} ${isLodgingRemoved ? "bg-red-50 text-red-400" : ""}`}
                  aria-label="Lodging cost"
                />
                {!isRowReadOnly && originalLodging > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (isLodgingRemoved) {
                        updateLineItem(item.id, 'lodgingCost', originalLodging);
                      } else {
                        updateLineItem(item.id, 'lodgingCost', 0);
                      }
                    }}
                    className={`shrink-0 w-9 h-9 lg:w-auto lg:h-auto inline-flex items-center justify-center rounded-lg lg:rounded-none text-sm ${isLodgingRemoved ? "text-green-600 hover:text-green-800" : "text-red-500 hover:text-red-700"}`}
                    title={isLodgingRemoved ? "Restore lodging" : "Remove lodging"}
                    aria-label={isLodgingRemoved ? "Restore lodging" : "Remove lodging"}
                  >
                    {isLodgingRemoved ? "↺" : "✕"}
                  </button>
                )}
              </div>
            );
          })()}
        </div>

        <div className="col-span-4 lg:col-span-1 order-3 lg:order-none lg:text-center font-semibold text-gray-800 text-xs tabular-nums line-item-usd" data-label="Running total" aria-label="Running total">
          {item.runningTotal?.toFixed(2) ?? '0.00'}
        </div>

        <div className="col-span-4 lg:col-span-1 order-3 lg:order-none" data-label="Comments">
          <textarea
            value={item.comments || ''}
            onChange={(e) => updateLineItem(item.id, 'comments', e.target.value)}
            className={`w-full px-3 lg:px-1 py-2 lg:py-1 text-sm lg:text-xs border border-gray-300 rounded-lg lg:rounded resize-none h-16 lg:h-10 ${isRowReadOnly ? "bg-gray-100 cursor-not-allowed" : ""}`}
            placeholder="Comments..."
            readOnly={isRowReadOnly}
            aria-label="Comments for this expense"
          />
        </div>

        {claimStatus !== 'approved' && (
          <div className="col-span-4 lg:col-span-1 order-3 lg:order-none">
            {!isRowReadOnly && (
              <div className="flex flex-row lg:flex-col items-center lg:items-stretch justify-between gap-3 lg:gap-2">
                <label className="flex items-center gap-2 lg:gap-1 min-h-[44px] lg:min-h-0 text-sm lg:text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(appliedRates && appliedRates[item.id]) || false}
                    onChange={() => apply75Percent(item.id)}
                    className="w-5 h-5 lg:w-3 lg:h-3 accent-stanford-red"
                    aria-label="Apply 75% rates"
                  />
                  <span><span className="lg:hidden">First/last day </span>75%</span>
                </label>

                <button
                  type="button"
                  className="min-h-[44px] lg:min-h-0 px-4 lg:px-2 py-1 text-sm lg:text-xs rounded-lg lg:rounded border text-red-600 hover:bg-red-100 border-red-300 transition-colors lg:mt-2"
                  onClick={async () => {
                    if (await confirm('Are you sure you want to delete this line item? This action cannot be undone.')) {
                      deleteLineItem(item.id);
                    }
                  }}
                  title="Delete this row"
                  aria-label="Delete this line item"
                >
Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

    </React.Fragment>
  );
};

// FIXED: Simplified DocumentUploader component with memoization
const DocumentUploader = React.memo(({ onUpload, isLoading, existingDocuments = [], onDelete, canDelete = false, noticeAudience = 'claimant' }) => {
  // Custom in-app dialogs replacing window.confirm/window.alert. `alert` is
  // named to shadow the global deliberately: every existing alert(...) call in
  // this file then routes through the styled modal with no other change.
  const { confirm, alert } = useDialog();
  const [files, setFiles] = useState([]);
  const [fileDescriptions, setFileDescriptions] = useState({});
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = useRef(null);
  const { claimId } = useParams();

  const handleFileChange = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);

    const newDescriptions = {};
    selectedFiles.forEach((file, index) => {
      newDescriptions[index] = fileDescriptions[index] || '';
    });
    setFileDescriptions(newDescriptions);
  }, [fileDescriptions]);

  const handleDescriptionChange = useCallback((index, description) => {
    setFileDescriptions(prev => ({
      ...prev,
      [index]: description
    }));
  }, []);

  const handleUpload = useCallback(async () => {
    if (files.length === 0) {
      alert("Please select at least one file to upload");
      return;
    }

    const missingDescriptions = files.some((file, index) =>
      !fileDescriptions[index] || fileDescriptions[index].trim() === ''
    );

    if (missingDescriptions) {
      alert("Please provide descriptions for all selected files");
      return;
    }

    setUploading(true);
    try {
      const filesWithDescriptions = files.map((file, index) => ({
        file,
        description: fileDescriptions[index].trim()
      }));

      await onUpload(filesWithDescriptions);
      setFiles([]);
      setFileDescriptions({});
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Failed to upload documents. Please try again.");
    } finally {
      setUploading(false);
    }
  }, [files, fileDescriptions, onUpload, alert]);

  const handleDelete = useCallback(async (docId, docName) => {
    if (!docId || !claimId) {
      alert('Error: Cannot delete document - invalid ID');
      return;
    }

    if (!await confirm(`Are you sure you want to delete "${docName}"?`)) {
      return;
    }

    setDeleting(docId);
    try {
      await onDelete(docId);
    } catch (error) {
      console.error("Delete failed:", error);
      alert(`Failed to delete document: ${error.message}`);
    } finally {
      setDeleting(null);
    }
  }, [onDelete, claimId, alert, confirm]);

  const removeFile = useCallback((indexToRemove) => {
    setFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    setFileDescriptions(prev => {
      const newDescriptions = {};
      Object.keys(prev).forEach(key => {
        const index = parseInt(key);
        if (index < indexToRemove) {
          newDescriptions[index] = prev[key];
        } else if (index > indexToRemove) {
          newDescriptions[index - 1] = prev[key];
        }
      });
      return newDescriptions;
    });
  }, []);

  return (
    <div className="space-y-6">
      {existingDocuments && existingDocuments.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold text-gray-800 mb-3">Uploaded Documents ({existingDocuments.length})</h4>
          <div className="space-y-2">
            {existingDocuments.map((doc, index) => (
              <div key={doc.id || index} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="hidden sm:block text-2xl">
                  {doc.fileType === 'pdf' ? '' : ''}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate" title={doc.fileName}>
                    {doc.fileName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {doc.description}
                  </p>
                  <p className="text-xs text-gray-400">
                    Uploaded: {new Date(doc.uploadedAt || doc.createdAt).toLocaleDateString()}
                  </p>
                  {isInfected(doc) && <InfectedFileNotice audience={noticeAudience} className="mt-1" />}
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  {/* Removed by the virus scan: no link (the fallback route
                      would only 404). Delete stays so the row can be cleared. */}
                  {!isInfected(doc) && (
                    <a
                      href={doc.fileUrl || `/claims/${doc.claimId}/documents/${doc.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex flex-1 sm:flex-initial items-center justify-center min-h-[44px] sm:min-h-0 px-3 py-1 text-sm text-stanford-red border border-stanford-red rounded hover:bg-stanford-red hover:text-white transition-colors"
                    >
                      View
                    </a>
                  )}
                  {canDelete && (
                    <button
                      className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 px-3 py-1 text-sm text-red-500 border border-red-300 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                      onClick={() => handleDelete(doc.id || doc._id, doc.fileName)}
                      disabled={deleting === (doc.id || doc._id)}
                      title="Delete document"
                      aria-label={`Delete ${doc.fileName || 'document'}`}
                    >
                      {deleting === (doc.id || doc._id) ? '...' : (
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                          <path d="M10 11v6M14 11v6" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h4 className="text-lg font-semibold text-gray-800 mb-2">Upload New Documents</h4>
        <p className="text-sm text-gray-600 mb-4">Upload receipts or documentation that support this claim.</p>

        <div className="space-y-4">
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            accept=".pdf,.jpg,.jpeg,.png"
            ref={fileInputRef}
            disabled={isLoading || uploading}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-stanford-red file:text-white hover:file:bg-stanford-light-red disabled:opacity-50"
          />

          {files.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">New Files to Upload:</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {files.map((file, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg bg-white">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate" title={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}>
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </p>
                      </div>
                      <button
                        type="button"
                        className="ml-2 -mr-2 -mt-2 sm:mr-0 sm:mt-0 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 p-1 text-gray-400 hover:text-red-500 transition-colors"
                        onClick={() => removeFile(index)}
                        disabled={uploading}
                        title="Remove file"
                        aria-label={`Remove ${file.name}`}
                      >
                        X
                      </button>
                    </div>

                    <textarea
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                      placeholder="Enter description for this file..."
                      value={fileDescriptions[index] || ''}
                      onChange={(e) => handleDescriptionChange(index, e.target.value)}
                      disabled={uploading}
                      rows={3}
                      required
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {files.length > 0 && (
          <button
            className="mt-4 w-full sm:w-auto justify-center sm:justify-start px-6 py-3 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={handleUpload}
            disabled={isLoading || uploading || files.length === 0}
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Uploading...</span>
              </>
            ) : (
              `Upload ${files.length} Document${files.length !== 1 ? 's' : ''}`
            )}
          </button>
        )}
      </div>
    </div>
  );
});

// FIXED: Memoized DocumentManagement component
const DocumentManagement = React.memo(({
  claimId: _claimId,
  supportingDocuments,
  uploadSupportingDocuments,
  deleteSupportingDocument,
  isLoading,
  canDelete = false,
  noticeAudience = 'claimant'
}) => {
  const [showUploader, setShowUploader] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 max-sm:p-4 mt-4 sm:mt-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Supporting Documents</h3>
        <button
          className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 border border-stanford-red text-stanford-red rounded-lg hover:bg-stanford-red hover:text-white transition-colors disabled:opacity-50"
          onClick={() => setShowUploader(!showUploader)}
          disabled={isLoading}
        >
          {showUploader ? ' Hide Document Manager' : ' Manage Documents'}
        </button>
      </div>

      {showUploader && (
        <div className="border-t border-gray-200 pt-4">
          <DocumentUploader
            onUpload={uploadSupportingDocuments}
            onDelete={deleteSupportingDocument}
            isLoading={isLoading}
            existingDocuments={supportingDocuments}
            canDelete={canDelete}
            noticeAudience={noticeAudience}
          />
        </div>
      )}

      {!showUploader && supportingDocuments && supportingDocuments.length > 0 && (
        <div>
          <p className="text-sm text-gray-600 mb-3">{supportingDocuments.length} document{supportingDocuments.length !== 1 ? 's' : ''} uploaded</p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
            {supportingDocuments.map((doc, idx) => (
              <div key={idx} className={`flex items-center gap-2 px-3 py-0 sm:py-2 bg-gray-50 rounded-lg border border-gray-200 ${isInfected(doc) ? 'flex-wrap py-2' : ''}`}>
                <span className="hidden sm:inline text-lg">{doc.fileType === 'pdf' ? '' : ''}</span>
                <span className="flex-1 sm:flex-initial min-w-0 text-sm text-gray-700 sm:max-w-[150px] truncate" title={doc.fileName}>{doc.fileName}</span>
                {isInfected(doc) ? (
                  <InfectedFileNotice audience={noticeAudience} className="w-full" />
                ) : (
                  <a
                    href={doc.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center min-h-[44px] sm:min-h-0 px-2 sm:px-0 text-sm text-stanford-red hover:text-stanford-light-red underline"
                  >
                    View
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

// FIXED: Memoized ClaimMetadata component
const ClaimMetadata = React.memo(({ createdAt, approvedAt, approvedBy }) => {
  return (
    <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-4">
      <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-3">Claim Information</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        <div>
          <span className="text-sm text-gray-500">Created:</span>
          <p className="text-sm font-medium text-gray-800">
            {createdAt ? new Date(createdAt).toLocaleString() : 'N/A'}
          </p>
        </div>
        {approvedAt && (
          <div>
            <span className="text-sm text-gray-500">Approved On:</span>
            <p className="text-sm font-medium text-gray-800">
              {new Date(approvedAt).toLocaleString()}
            </p>
          </div>
        )}
        {approvedBy && (
          <div>
            <span className="text-sm text-gray-500">Approved By:</span>
            <p className="text-sm font-medium text-gray-800 break-words">{approvedBy}</p>
          </div>
        )}
      </div>
    </div>
  );
});

const PerDiemForm = ({ adminMode = false, onBehalfOf = null }) => {
  // Custom in-app dialogs replacing window.confirm/window.alert. `alert` is
  // named to shadow the global deliberately: every existing alert(...) call in
  // this file then routes through the styled modal with no other change.
  const { confirm, alert } = useDialog();
  // FIXED: Stabilized admin mode detection
  const {
    user, isAuditor, isRegionalDirector, isGlobalDirector, isFinanceDirector,
  } = useContext(AuthContext);

  // Auditors open claims through the same /admin/ routes, so admin mode must be
  // role-aware - otherwise they'd get the admin editing/allocation controls.
  const isAdminMode = useMemo(() => {
    if (isAuditor) return false;
    return adminMode || window.location.pathname.includes('/admin/');
  }, [adminMode, isAuditor]);

  // State management
  const [portalElement, setPortalElement] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [quickFillStartDate, setQuickFillStartDate] = useState('');
  const [quickFillEndDate, setQuickFillEndDate] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [formErrors, _setFormErrors] = useState([]);
  const [isQuickFillLoading, setIsQuickFillLoading] = useState(false);
  // Reported by ClaimPtaeAllocation. The claim's Save is blocked while it is
  // true: adjusting a split and then saving the claim without saving the split
  // silently discarded the adjustment.
  const [ptaeAllocationDirty, setPtaeAllocationDirty] = useState(false);
  // Published by ClaimPtaeAllocation so the Excel export can carry each line's
  // PTAE. useCallback because it is a dependency of that component's loader.
  const [ptaeAllocation, setPtaeAllocation] = useState(null);
  const handleAllocationLoaded = useCallback((a) => setPtaeAllocation(a), []);

  /**
   * Who may download the claim as a spreadsheet.
   *
   * Deliberately NOT isAdminMode: that flag governs EDITING and is forced false
   * for auditors, which left the people who review claims unable to export the
   * one artefact they check them against. Downloading is a read.
   */
  // Stacked quick-fill segments (one per city visited). The single input row is
  // the "entry" for the next city; the + button pushes it here, and Quick Fill
  // fetches rates for every stacked city (plus a filled entry) in one go.
  const [quickFillSegments, setQuickFillSegments] = useState([]);
  // Exclusions honoured by Quick Fill when it generates rows, and applied to any
  // rows that already exist.
  // Lines ticked for a bulk action.
  const [selectedLineIds, setSelectedLineIds] = useState(new Set());
  const [exclusions, setExclusions] = useState({
    lodging: false, breakfast: false, lunch: false, dinner: false,
  });

  // Refs
  const inputRefs = useRef({});
  const timeoutRef = useRef(null);

  const {
    formData: {
      lastName, setLastName,
      firstName, setFirstName,
      position, setPosition,
      businessPurpose, setBusinessPurpose,
      startDate, setStartDate,
      endDate, setEndDate,
    },
    lineItems, setLineItems,
    citySearch, setCitySearch,
    cityResults,
    activeSearchId, setActiveSearchId,
    isLoading,
    error,
    totalAmount,
    searchCities,
    addLineItem,
    updateLineItem,
    handleCitySelect,
    apply75Percent,
    deleteLineItem,
    restoreLineItem,
    handleKeyDown,
    saveForm,
    appliedRates,
    quickFillCity,
    quickFillCountry,
    setQuickFillCity,
    setQuickFillCountry,
    setCityResults,
    setError,
    handleNoLodgingAllToggle,
    handleMealExclusionToggle,
    isReadOnly,
    saveAdminComment,
    toggleAdminComment,
    showActionModal, setShowActionModal,
    selectedAction, setSelectedAction,
    adminDecisionComment, setAdminDecisionComment,
    handleAdminAction,
    withdrawApproval, handleModalSubmit,
    saveAdminChanges,
    isSaving, saveSuccess,
    claimStatus,
    claimData,
    adminComments,
    processedBy,
    processedAt,
    loadClaimData,
    deletePerDiemClaim,
    deleteLineItems,
    restoreLineItems,
    savedDraftId,
    uploadSupportingDocuments, supportingDocuments,
    deleteSupportingDocument,
    handleCustomCityCancel,
    customCityCountry,
    customCityName,
    setCustomCityCountry,
    setCustomCityName,
    handleCustomCitySubmit,
    showCustomCityInput,
    setShowCustomCityInput,
    setDebouncedSearch
  } = usePerDiemForm(isAdminMode, { onBehalfOf }); // FIXED: Use stabilized admin mode

  const canDeleteDocuments = isAdminMode && claimStatus !== 'approved';

  const downloadPDF = async () => {
    const { jsPDF }             = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    const {
      loadLogo, drawHeader, drawPageHeader, drawFooter, drawInfoBlock, drawApprovalChain,
      STANFORD_RED, LIGHT_GRAY, MID_GRAY,
    } = await import('../../utils/claimPdfUtils.mjs');

    const doc    = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW  = doc.internal.pageSize.getWidth();
    const pageH  = doc.internal.pageSize.getHeight();
    const margin = 12;
    const TITLE  = 'PER DIEM & LODGING CLAIM';

    const logoDataUrl = await loadLogo(seedlogo);
    let y = drawHeader(doc, logoDataUrl, TITLE, pageW, margin);

    // -- Claim metadata ----------------------------------------------------
    const ownerName = [
      claimData?.user?.firstName || claimData?.firstName || user?.firstName || firstName || '',
      claimData?.user?.lastName  || claimData?.lastName  || user?.lastName  || lastName  || '',
    ].filter(Boolean).join(' ');
    const ownerPosition   = claimData?.user?.position || claimData?.position || user?.position || position || '';
    const claimIdDisplay  = claimId || claimData?.claimId || claimData?._id || '-';
    const statusDisplay   = (claimStatus || 'draft').toUpperCase();
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }) : '-';

    const approvedByName = processedBy
      ? String(processedBy)
      : (claimStatus === 'approved' ? 'On file' : '-');
    const _approvedDate = processedAt ? fmtDate(processedAt) : '-';

    y = drawInfoBlock(doc, [
      ['Claim Owner:',      ownerName,                  'Claim ID:',      claimIdDisplay],
      ['Position / Title:', ownerPosition,               'Status:',        statusDisplay],
      ['Business Purpose:', businessPurpose || '-',      'Total Amount:',  `$${(totalAmount || 0).toFixed(2)}`],
      ['Travel Period:',    `${fmtDate(startDate)} - ${fmtDate(endDate)}`, 'Approved By:', approvedByName],
    ], y, pageW, margin);

    // -- Daily details table -----------------------------------------------
    const columns = [
      { header: '#',            dataKey: 'num' },
      { header: 'Date',         dataKey: 'date' },
      { header: 'City',         dataKey: 'city' },
      { header: 'Country',      dataKey: 'country' },
      { header: 'Breakfast',    dataKey: 'breakfast' },
      { header: 'Lunch',        dataKey: 'lunch' },
      { header: 'Dinner',       dataKey: 'dinner' },
      { header: 'Incidentals',  dataKey: 'incidentals' },
      { header: 'Total M&IE',   dataKey: 'totalMIE' },
      { header: 'Lodging',      dataKey: 'lodging' },
      { header: 'Running Total',dataKey: 'runningTotal' },
      { header: 'Comments',     dataKey: 'comments' },
    ];

    const tableRows = lineItems.map((item, idx) => ({
      num:          idx + 1,
      date:         fmtDate(item.date),
      city:         item.city || '-',
      country:      item.country || '-',
      breakfast:    `$${(Number(item.breakfast)   || 0).toFixed(2)}`,
      lunch:        `$${(Number(item.lunch)        || 0).toFixed(2)}`,
      dinner:       `$${(Number(item.dinner)       || 0).toFixed(2)}`,
      incidentals:  `$${(Number(item.incidentals)  || 0).toFixed(2)}`,
      totalMIE:     `$${(Number(item.totalMIE)     || 0).toFixed(2)}`,
      lodging:      `$${(Number(item.lodgingCost)  || 0).toFixed(2)}`,
      runningTotal: `$${(Number(item.runningTotal) || 0).toFixed(2)}`,
      comments:     item.adminComments || item.comments || '',
    }));

    autoTable(doc, {
      startY: y,
      head: [columns.map(c => c.header)],
      body: tableRows.map(r => columns.map(c => r[c.dataKey])),
      styles: { fontSize: 7.5, cellPadding: 2, overflow: 'linebreak', lineColor: MID_GRAY, lineWidth: 0.2 },
      headStyles: { fillColor: STANFORD_RED, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8, halign: 'center' },
      alternateRowStyles: { fillColor: LIGHT_GRAY },
      columnStyles: {
        0:  { halign: 'center', cellWidth: 7 },
        1:  { cellWidth: 22 },
        2:  { cellWidth: 24 },
        3:  { cellWidth: 22 },
        4:  { halign: 'right', cellWidth: 18 },
        5:  { halign: 'right', cellWidth: 16 },
        6:  { halign: 'right', cellWidth: 16 },
        7:  { halign: 'right', cellWidth: 20 },
        8:  { halign: 'right', cellWidth: 20, fontStyle: 'bold' },
        9:  { halign: 'right', cellWidth: 18 },
        10: { halign: 'right', cellWidth: 24, fontStyle: 'bold' },
        11: { cellWidth: 'auto' },
      },
      margin: { left: margin, right: margin },
      didDrawPage: (data) => { if (data.pageNumber > 1) drawPageHeader(doc, TITLE, pageW, margin, data.pageNumber); },
    });

    // -- Total -------------------------------------------------------------
    const finalY = doc.lastAutoTable.finalY + 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...STANFORD_RED);
    doc.text(`TOTAL CLAIM: $${(totalAmount || 0).toFixed(2)} USD`, pageW - margin, finalY, { align: 'right' });

    // -- Approval history --------------------------------------------------
    // If not enough vertical room, push onto a new page
    let chainStartY = finalY + 10;
    if (chainStartY > pageH - 50) {
      doc.addPage();
      drawPageHeader(doc, TITLE, pageW, margin, doc.internal.pages.length - 1);
      chainStartY = 16;
    }
    drawApprovalChain(doc, claimData?.approvalChain, chainStartY, pageW, margin, {
      name:   processedBy ? String(processedBy) : null,
      role:   'admin',
      date:   processedAt,
      status: claimStatus,
    });

    // -- Footer ------------------------------------------------------------
    drawFooter(doc, claimIdDisplay, pageH, pageW);

    doc.save(`PerDiem_Claim_${claimIdDisplay}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const isCreateRoute = useMemo(() => {
    return window.location.pathname.includes('/create');
  }, []);

  const { claimId } = useParams();

  const isClaimReviewer = isAuditor || isRegionalDirector || isGlobalDirector || isFinanceDirector;
  const canDownloadClaimExcel = !!claimId && (isAdminMode || isClaimReviewer);

  /**
   * Show the PTA allocation to reviewers too, read-only.
   *
   * Two reasons, not one: an auditor checking a claim needs to see which code
   * each day is funded from, AND this component is what publishes that mapping
   * to the Excel export - gated on isAdminMode alone it never mounted for them,
   * so their download would have carried an empty PTAE column.
   */
  const showPtaeAllocation = !!claimId && (isAdminMode || isClaimReviewer);

  // FIXED: Prevent constant reloads with better dependency management
  useEffect(() => {
    if (claimId && !claimData) { // Only load if we don't have data yet
      loadClaimData(claimId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]); // loadClaimData and claimData intentionally omitted

  const formatDateForInput = useCallback((date) => formatLocalDate(date), []);

  const parseInputDate = useCallback((dateString) => parseLocalDate(dateString), []);

  const validateLineItem = useCallback((item) => {
    const errors = [];
    if (!item.date) errors.push('Date is required');
    if (!item.city) errors.push('City is required');

    if (item.date && isFutureDate(item.date)) {
      errors.push('Date cannot be in the future');
    }

    if (item.date && !isDateWithinTravelRange(item.date, startDate, endDate)) {
      errors.push('Date must be within travel dates');
    }

    return errors;
  }, [startDate, endDate]);

  const _validateForm = useCallback(() => {
    const errors = [];

    if (!lastName || !firstName) errors.push('Name is required');
    if (!businessPurpose) errors.push('Business purpose is required');
    if (!startDate || !endDate) errors.push('Travel dates are required');

    if (startDate && isFutureDate(startDate)) {
      errors.push('Travel start date cannot be in the future');
    }
    if (endDate && isFutureDate(endDate)) {
      errors.push('Travel end date cannot be in the future');
    }

    lineItems.forEach((item, index) => {
      const itemErrors = validateLineItem(item);
      if (itemErrors.length > 0) {
        errors.push(`Line item ${index + 1}: ${itemErrors.join(', ')}`);
      }
    });

    const dateMap = new Map();
    lineItems.forEach((item, index) => {
      if (item.date) {
        const dateStr = new Date(item.date).toISOString().split('T')[0];
        if (dateMap.has(dateStr)) {
          errors.push(`Duplicate date found: ${dateStr}`);
        } else {
          dateMap.set(dateStr, index);
        }
      }
    });

    return errors;
  }, [lastName, firstName, businessPurpose, startDate, endDate, lineItems, validateLineItem]);

  // Portal management - minimal container that doesn't block clicks
  useEffect(() => {
    let element = document.getElementById('city-dropdown-portal');

    if (!element) {
      element = document.createElement('div');
      element.id = 'city-dropdown-portal';
      // Don't make it full-screen - just a minimal container for the dropdown
      element.style.position = 'relative';
      element.style.zIndex = '999998';
      document.body.appendChild(element);
    }

    setPortalElement(element);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const positionDropdown = useCallback((itemId) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      let inputElement;

      if (itemId === 'quickFillCity') {
        inputElement = document.getElementById('quickFillCityInput');
      } else if (itemId === 'quickFillCountry') {
        inputElement = document.getElementById('quickFillCountryInput');
      } else {
        inputElement = inputRefs.current[itemId];
      }

      if (!inputElement) {
        return;
      }

      const rect = inputElement.getBoundingClientRect();
      const top = rect.bottom + 5;
      // Keep the list on screen: on a narrow phone the 300px minimum can run
      // past the right edge, so cap the width and pull it back inside an 8px
      // margin. Wide screens are unaffected (the input already fits).
      const viewportWidth = window.innerWidth;
      const width = Math.min(Math.max(rect.width, 300), viewportWidth - 16);
      const left = Math.max(8, Math.min(rect.left, viewportWidth - 8 - width));

      const position = {
        top,
        left,
        width
      };

      setDropdownPosition(position);
    }, 0);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (activeSearchId) {
        positionDropdown(activeSearchId);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [activeSearchId, positionDropdown]);

  // Debounce the network search so typing a name fires one request after a short
  // pause, not one per keystroke. The handlers below still set activeSearchId /
  // citySearch / dropdown position synchronously - only the searchCities() API call
  // is delayed - so there's no race with activeSearchId.
  const searchDebounceRef = useRef(null);
  const debouncedSearchCities = useCallback((value, id) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      searchCities(value, id);
    }, 350);
  }, [searchCities]);
  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);

  const handleCityInputChange = useCallback((itemId, value) => {
    setCitySearch(value);
    setActiveSearchId(itemId);

    if (value === '') {
      updateLineItem(itemId, 'city', '');
      updateLineItem(itemId, 'country', '');
      setCityResults([]);
    } else if (value.length >= 2) {
      // Debounced: fires one request after the user pauses, not per keystroke
      debouncedSearchCities(value, itemId);
    } else {
      setCityResults([]); // Clear results if less than 2 characters
    }

    positionDropdown(itemId);
  }, [setCitySearch, setActiveSearchId, updateLineItem, setCityResults, debouncedSearchCities, positionDropdown]);


  const handleQuickFillCountryInputChange = useCallback((value) => {
    setCountrySearch(value);
    setActiveSearchId('quickFillCountry');

    setQuickFillCity('');
    setCitySearch('');

    if (value === '') {
      setQuickFillCountry('');
      setCityResults([]);
    } else if (value.length >= 2) {
      // Debounced network call; activeSearchId is set synchronously above, no race
      debouncedSearchCities(value, 'quickFillCountry');
    } else {
      setCityResults([]);
    }

    positionDropdown('quickFillCountry');
  }, [setCountrySearch, setActiveSearchId, setQuickFillCity, setCitySearch, setQuickFillCountry, setCityResults, debouncedSearchCities, positionDropdown]);
const handleQuickFillCountrySelect = useCallback((countryName) => {

  // IMPORTANT: Set BOTH countrySearch AND quickFillCountry to the selected value
  // This ensures the input shows the value regardless of React state batching order
  // Input logic: value={activeSearchId === 'quickFillCountry' ? countrySearch : quickFillCountry}
  setCountrySearch(countryName);  // Shows if activeSearchId is still 'quickFillCountry'
  setQuickFillCountry(countryName);  // Shows once activeSearchId becomes null
  setQuickFillCity('');

  // Clear other state
  setActiveSearchId(null);
  setCitySearch('');
  setCityResults([]);

  // Blur input to close any mobile keyboards
  const input = document.getElementById('quickFillCountryInput');
  if (input) {
    input.blur();
  }
}, [setCountrySearch, setQuickFillCountry, setQuickFillCity, setActiveSearchId, setCitySearch, setCityResults]);


  const handleCityInputFocus = useCallback((itemId) => {
    if (claimStatus === 'approved') {
      return;
    }

    setActiveSearchId(itemId);
    const currentCity = lineItems?.find(item => item.id === itemId)?.city || '';
    setCitySearch(currentCity);

    if (currentCity && currentCity.length >= 2) {
      setDebouncedSearch(currentCity);
    }

    setTimeout(() => { positionDropdown(itemId);
    }, 50);
  }, [setActiveSearchId, setCitySearch, lineItems, claimStatus, setDebouncedSearch, positionDropdown]);

  const handleAddLineItem = useCallback(() => {
    if (!startDate || !endDate) {
      alert('Please set travel dates before adding line items.');
      return;
    }

    if (isFutureDate(startDate) || isFutureDate(endDate)) {
      alert('Cannot add line items for future travel dates.');
      return;
    }

    // A new line takes the first day of the trip that has none. When every day
    // already has one there is no day left to give it - it used to take the
    // day AFTER the trip, a date outside the travel dates that nothing caught
    // until submit.
    const tripStart = startOfDay(startDate);
    const tripEnd = startOfDay(endDate);
    const used = new Set(
      (lineItems || [])
        .map(i => startOfDay(i.date))
        .filter(d => d && d >= tripStart && d <= tripEnd)
        .map(d => d.getTime())
    );
    if (tripStart && tripEnd && used.size >= inclusiveDayCount(tripStart, tripEnd)) {
      alert(
        `Every day of the trip (${formatLocalDate(tripStart)} to ${formatLocalDate(tripEnd)}) already has a line. `
        + 'To claim another day, extend the travel dates first.'
      );
      return;
    }

    addLineItem();
  }, [startDate, endDate, addLineItem, alert, lineItems]);

  // Validate a single quick-fill segment (dates in range, not future, ordered).
  // Returns an error string, or null when valid.
  const validateQuickFillSegment = useCallback((seg) => {
    if (!seg.startDate || !seg.endDate || !seg.city || !seg.country) {
      return "Please fill in date range, country and city";
    }
    const qStart = parseLocalDate(seg.startDate);
    const qEnd = parseLocalDate(seg.endDate);
    if (isFutureDate(qStart) || isFutureDate(qEnd)) {
      return "Quick fill dates cannot be in the future";
    }
    if (!isDateWithinTravelRange(qStart, startDate, endDate) ||
        !isDateWithinTravelRange(qEnd, startDate, endDate)) {
      return "Quick fill dates must be within your travel dates";
    }
    if (qEnd < qStart) {
      return "End date must be after start date";
    }
    return null;
  }, [startDate, endDate]);

  // Every day a segment would cover, as YYYY-MM-DD labels.
  const segmentDays = useCallback((seg) => {
    const start = parseLocalDate(seg.startDate);
    const end = parseLocalDate(seg.endDate);
    if (!start || !end || end < start) return [];
    const days = [];
    const count = inclusiveDayCount(start, end);
    for (let i = 0; i < count; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      days.push(formatLocalDate(d));
    }
    return days;
  }, []);

  // Which of this segment's days are already spoken for - by a line already on
  // the form, or by a city stacked earlier. Quick Fill silently skipped these,
  // so an overlapping range just produced fewer rows than the user expected
  // with nothing explaining why.
  const findSegmentDayConflicts = useCallback((seg, otherSegments = []) => {
    const taken = new Map();
    (lineItems || []).forEach(item => {
      const d = startOfDay(item.date);
      if (d) taken.set(formatLocalDate(d), item.city || 'an existing line');
    });
    otherSegments.forEach(other => {
      segmentDays(other).forEach(day => {
        if (!taken.has(day)) taken.set(day, other.city);
      });
    });
    return segmentDays(seg)
      .filter(day => taken.has(day))
      .map(day => `${day} (${taken.get(day)})`);
  }, [lineItems, segmentDays]);

  // + button: stack the currently-entered city as a segment and clear the entry
  // inputs so the user can add the next city.
  const handleAddQuickFillSegment = useCallback(() => {
    const seg = {
      startDate: quickFillStartDate,
      endDate: quickFillEndDate,
      city: quickFillCity,
      country: quickFillCountry,
    };
    const validationError = validateQuickFillSegment(seg);
    if (validationError) {
      setError(validationError);
      return;
    }
    const conflicts = findSegmentDayConflicts(seg, quickFillSegments);
    if (conflicts.length > 0) {
      setError(
        `These days already have a per diem line: ${conflicts.join(', ')}. ` +
        `Each day can appear only once - change this city's date range, or remove the existing lines first.`
      );
      return;
    }
    setQuickFillSegments(prev => [...prev, { id: crypto.randomUUID(), ...seg }]);
    setQuickFillStartDate('');
    setQuickFillEndDate('');
    setQuickFillCity('');
    setQuickFillCountry('');
    setCitySearch('');
    setCountrySearch('');
    setActiveSearchId(null);
    setError('');
  }, [quickFillStartDate, quickFillEndDate, quickFillCity, quickFillCountry, quickFillSegments, findSegmentDayConflicts, validateQuickFillSegment, setError, setQuickFillCity, setQuickFillCountry, setCitySearch, setCountrySearch, setActiveSearchId]);

  const handleRemoveQuickFillSegment = useCallback((id) => {
    setQuickFillSegments(prev => prev.filter(s => s.id !== id));
  }, []);

  const handleQuickFill = useCallback(async () => {
    // Assemble every city to fetch: the stacked segments plus the current entry
    // row when it's fully filled in.
    const segments = [...quickFillSegments];
    if (quickFillStartDate && quickFillEndDate && quickFillCity && quickFillCountry) {
      segments.push({
        id: 'current',
        startDate: quickFillStartDate,
        endDate: quickFillEndDate,
        city: quickFillCity,
        country: quickFillCountry,
      });
    }

    if (segments.length === 0) {
      setError("Add at least one city (date range + country + city) to quick fill");
      return;
    }

    // Validate all segments up front so we fail fast before any network calls.
    // Each is also checked against the ones before it, so an overlap is reported
    // rather than quietly dropped when the days are assembled below.
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const validationError = validateQuickFillSegment(seg);
      if (validationError) {
        setError(validationError);
        return;
      }
      const conflicts = findSegmentDayConflicts(seg, segments.slice(0, i));
      if (conflicts.length > 0) {
        setError(
          `${seg.city || 'This city'} covers days that already have a per diem line: ` +
          `${conflicts.join(', ')}. Each day can appear only once - adjust the date ranges ` +
          `or remove the existing lines first.`
        );
        return;
      }
    }

    setIsQuickFillLoading(true);

    try {
      // Shared across segments so the same day is never added twice, whether it
      // already exists on the form or is claimed by an earlier segment.
      const existingDates = new Set(lineItems.map(item => {
        const date = new Date(item.date);
        return date.toISOString().split('T')[0];
      }));

      const newLineItems = [];

      for (const seg of segments) {
        const qStart = parseLocalDate(seg.startDate);
        const qEnd = parseLocalDate(seg.endDate);
        const dayDiff = inclusiveDayCount(qStart, qEnd);

        for (let i = 0; i < dayDiff; i++) {
          const currentDate = new Date(qStart);
          currentDate.setDate(qStart.getDate() + i);
          // Local components, not toISOString(): qStart is local midnight, and
          // ahead of UTC that converts to the previous day - which shifted every
          // quick-filled row's date back one and fetched the wrong season's rate
          // at a month boundary.
          const isoDate = formatLocalDate(currentDate);

          if (existingDates.has(isoDate)) {
            continue;
          }
          existingDates.add(isoDate);

          const rates = await PerDiemService.getPerDiemRates(isoDate, seg.city, seg.country);

          // Honour the exclusions chosen above, so a quick-filled row arrives
          // already correct instead of needing the toggle re-applied after.
          const breakfast = exclusions.breakfast ? 0 : (Number(rates.breakfast) || 0);
          const lunch = exclusions.lunch ? 0 : (Number(rates.lunch) || 0);
          const dinner = exclusions.dinner ? 0 : (Number(rates.dinner) || 0);
          const incidentals = Number(rates.incidentals) || 0;
          const lodging = exclusions.lodging ? 0 : (Number(rates.lodging) || 0);
          const totalMIE = breakfast + lunch + dinner + incidentals;

          newLineItems.push({
            id: crypto.randomUUID(),
            date: currentDate,
            city: `${seg.city}, ${seg.country}`,
            country: seg.country,
            stateDeptMIE: rates.mie,
            breakfast,
            lunch,
            dinner,
            incidentals,
            totalMIE,
            lodgingCost: lodging,
            // Untouched State Department figures, so unticking an exclusion
            // restores the real rate rather than the zero we wrote.
            originalRates: {
              breakfast: Number(rates.breakfast) || 0,
              lunch: Number(rates.lunch) || 0,
              dinner: Number(rates.dinner) || 0,
              incidentals,
              lodgingCost: Number(rates.lodging) || 0
            },
            // Which seasonal rate the date resolved to. Locations like Mumbai
            // publish two, so without this the applied figure is unexplained.
            // Display-only - it is not sent to the backend.
            rateSeason: rates.season || null,
            rateMonth: rates.dataMonth || null,
            // Same DoD manual-entry case as the per-row lookup; quick-fill has to
            // flag it too or a multi-segment trip through Honolulu silently fills
            // zeros for those days.
            manualEntryRequired: !!rates.manualEntryRequired,
            rateNotice: rates.manualEntryRequired ? (rates.message || null) : null,
          });
        }
      }

      if (newLineItems.length === 0) {
        setError("All selected dates already exist in the form.");
        return;
      }

      setLineItems(prevItems => {
        const combined = [...prevItems, ...newLineItems];

        combined.sort((a, b) => new Date(a.date) - new Date(b.date));

        let runningTotal = 0;
        return combined.map(item => {
          const mie = parseFloat(item.totalMIE) || 0;
          const lodging = parseFloat(item.lodgingCost) || 0;
          runningTotal += mie + lodging;
          return { ...item, runningTotal };
        });
      });

      setQuickFillSegments([]);
      setQuickFillStartDate('');
      setQuickFillEndDate('');
      setQuickFillCity('');
      setQuickFillCountry('');
      setError('');

    } catch (error) {
      console.error('Failed to fetch rates:', error);
      setError(`Failed to fetch per diem rates. Please try again.`);
    } finally {
      setIsQuickFillLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickFillSegments, quickFillStartDate, quickFillEndDate, quickFillCity, quickFillCountry, lineItems, setLineItems, setError, validateQuickFillSegment, findSegmentDayConflicts, exclusions]);

  const handleQuickFillCitySelect = useCallback((cityName, countryName) => {
    if (cityName === 'Other') {
      setCustomCityCountry(countryName);
      setCustomCityName('');
      setShowCustomCityInput('quickFillCity');
      // Set BOTH citySearch AND quickFillCity to handle React state batching
      const otherValue = `Other (${countryName})`;
      setCitySearch(otherValue);
      setQuickFillCity(otherValue);
      setCityResults([]);
      setActiveSearchId(null);

      const input = document.getElementById('quickFillCityInput');
      if (input) {
        input.blur();
      }
      return;
    }

    // IMPORTANT: Set BOTH citySearch AND quickFillCity to the selected value
    // This ensures the input shows the value regardless of React state batching order
    // Input logic: value={activeSearchId === 'quickFillCity' ? citySearch : quickFillCity}
    setCitySearch(cityName);  // Shows if activeSearchId is still 'quickFillCity'
    setQuickFillCity(cityName);  // Shows once activeSearchId becomes null
    setQuickFillCountry(countryName);
    setCityResults([]);
    setActiveSearchId(null);

    const input = document.getElementById('quickFillCityInput');
    if (input) {
      input.blur();
    }
  }, [setCitySearch, setQuickFillCity, setQuickFillCountry, setActiveSearchId, setCustomCityCountry, setCustomCityName, setShowCustomCityInput, setCityResults]);

  const handleQuickFillCityInputChange = useCallback((value) => {
    setCitySearch(value);
    setActiveSearchId('quickFillCity');

    if (value === '') {
      setQuickFillCity('');
      setCityResults([]);
    } else if (value.length >= 2) {
      // Debounced network call; activeSearchId is set synchronously above, no race
      debouncedSearchCities(value, 'quickFillCity');
    } else {
      setCityResults([]);
    }

    positionDropdown('quickFillCity');
  }, [setCitySearch, setActiveSearchId, setQuickFillCity, debouncedSearchCities, positionDropdown, setCityResults]);

  const effectiveIsReadOnly = isReadOnly || claimStatus === 'approved';

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInputClick = event.target.closest('.city-search input') ||
                          event.target.id === 'quickFillCityInput';

      if (!isInputClick) {
        setActiveSearchId(null);
      }
    };

    if (activeSearchId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeSearchId, setActiveSearchId]);

  // FIXED: Stabilized line items sorting to prevent infinite loops
  // Calendar days covered by more than one line, for the warning banner.
  const duplicateDayLabels = useMemo(() => {
    const seen = new Set();
    const dupes = new Set();
    for (const item of lineItems) {
      const day = startOfDay(item.date);
      if (!day) continue;
      const key = day.getTime();
      if (seen.has(key)) dupes.add(formatLocalDate(day));
      else seen.add(key);
    }
    return [...dupes].sort();
  }, [lineItems]);

  // Lines outside the travel dates - after the dates are changed, most often.
  // Shown as soon as it happens rather than only when Submit refuses.
  const outsideTripLabels = useMemo(() => {
    if (!startDate || !endDate) return [];
    const out = new Set();
    for (const item of lineItems) {
      if (item.date && !isDateWithinTravelRange(item.date, startDate, endDate)) {
        const day = startOfDay(item.date);
        if (day) out.add(formatLocalDate(day));
      }
    }
    return [...out].sort();
  }, [lineItems, startDate, endDate]);

  const sortedLineItems = useMemo(() => {
    if (lineItems.length === 0) return [];

    const sorted = [...lineItems].sort((a, b) => new Date(a.date) - new Date(b.date));

    let runningTotal = 0;
    return sorted.map(item => {
      const mie = parseFloat(item.totalMIE) || 0;
      const lodging = parseFloat(item.lodgingCost) || 0;
      runningTotal += mie + lodging;
      return { ...item, runningTotal };
    });
  }, [lineItems]);

  // FIXED: Only update if sorted items have actually changed
  useEffect(() => {
    if (sortedLineItems.length > 0) {
      const currentOrder = lineItems.map(item => item.id).join(',');
      const sortedOrder = sortedLineItems.map(item => item.id).join(',');

      if (currentOrder !== sortedOrder ||
          JSON.stringify(lineItems) !== JSON.stringify(sortedLineItems)) {
        setLineItems(sortedLineItems);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedLineItems]); // lineItems and setLineItems intentionally omitted

  // Each day's value as the form has it now (M&IE + lodging, the figure the
  // claim total and every PTAE bucket are built from), so the allocation panel
  // follows an edit before it is saved rather than showing the stored amount.
  const liveLineValues = useMemo(() => Object.fromEntries(
    (lineItems || []).map(item => [
      String(item.id),
      Math.round(((Number(item.totalMIE) || 0) + (Number(item.lodgingCost) || 0)) * 100) / 100,
    ])
  ), [lineItems]);

  // FIXED: Memoized supporting documents display to prevent duplicates
  const supportingDocumentsDisplay = useMemo(() => {
    if (!supportingDocuments || supportingDocuments.length === 0) return null;

    // Remove duplicates based on ID or filename
    const uniqueDocs = supportingDocuments.filter((doc, index, self) =>
      index === self.findIndex(d => (d.id || d.fileName) === (doc.id || doc.fileName))
    );

    return uniqueDocs;
  }, [supportingDocuments]);

  // The page frame (components/PageShell.mjs): photo band + title, content
  // column. Exactly one per route, so it belongs to whichever page the route
  // renders. The two wrapper pages - AdminPerDiemEdit (passes adminMode) and
  // PerDiemOnBehalf (passes onBehalfOf) - render their own PageShell with their
  // own title, so with either PROP set the form renders its content bare. The
  // routes that render the form directly pass neither and get the frame here.
  // Keyed on the props, not isAdminMode: the unwrapped
  // /admin/claim/perdiem-lodging route is in admin mode but has no wrapper.
  const isEmbedded = adminMode || !!onBehalfOf;
  const viewingOthersClaim = !!(claimData?.user && claimData.user.id !== user?.id);
  // Same titles as the expense form: admin mode edits, a reviewer reads
  // someone else's claim, everyone else is filing their own.
  const pageTitle = isAdminMode
    ? 'Admin Edit: Per Diem Claim'
    : (isClaimReviewer && (window.location.pathname.includes('/admin/') || viewingOthersClaim))
      ? 'Per Diem Claim'
      : 'Per Diem & Lodging';
  const Frame = isEmbedded ? React.Fragment : PageShell;
  // No photo on a form: the band is a title bar here, not a welcome.
  const frameProps = isEmbedded ? {} : { title: pageTitle, width: '7xl', photo: false };

  return (
    <Frame {...frameProps}>
        {/* Filing for someone else. Said at the top, in the claimant's name,
            because everything below looks exactly like the admin's own claim
            form - which is the point, and also the risk. It is the first
            surface under the band (PerDiemOnBehalf owns the frame), styled
            like the expense form's on-behalf box. */}
        {onBehalfOf && (
          <div className="mb-6 max-sm:mb-4 bg-indigo-50 border border-indigo-200 text-indigo-900 dark:bg-indigo-950 dark:border-indigo-800 dark:text-indigo-100 rounded-xl shadow-lg px-4 py-3 text-sm">
            <p className="font-semibold">
              Filing a per diem claim for {onBehalfOf.firstName} {onBehalfOf.lastName}
            </p>
            <p className="mt-0.5 text-indigo-800 dark:text-indigo-200">
              It will be their claim and appear in their dashboard. Submitting sends it
              for review and emails them to say you filed it.
            </p>
          </div>
        )}
        {/* Custom City Input Modal */}
        {showCustomCityInput && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-0" onClick={handleCustomCityCancel}>
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full sm:mx-4 p-6 max-sm:p-5 flex flex-col max-h-[calc(100dvh-1.5rem)]" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4 shrink-0">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Enter Custom City</h3>
                <button
                  className="p-2 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 -mr-2 sm:mr-0 text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={handleCustomCityCancel}
                  aria-label="Close"
                >
                  X
                </button>
              </div>

              <div className="space-y-4 min-h-0 overflow-y-auto -m-1 p-1">
                <div>
                  <label htmlFor="customCityInput" className="block text-sm font-medium text-gray-700 mb-1">City Name:</label>
                  <input
                    id="customCityInput"
                    type="text"
                    value={customCityName}
                    onChange={(e) => setCustomCityName(e.target.value)}
                    placeholder="Enter city name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleCustomCitySubmit();
                      } else if (e.key === 'Escape') {
                        handleCustomCityCancel();
                      }
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country:</label>
                  <input
                    type="text"
                    value={customCityCountry}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> This city will be added as "{customCityName.trim()}, {customCityCountry}".
                    Per diem rates may not be available for custom cities.
                  </p>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-3 mt-5 sm:mt-6 shrink-0">
                <button
                  onClick={handleCustomCityCancel}
                  className="flex-1 min-h-[44px] sm:min-h-0 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCustomCitySubmit}
                  className="flex-1 min-h-[44px] sm:min-h-0 px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!customCityName.trim() || isLoading}
                >
                  {isLoading ? 'Adding...' : 'Add City'}
                </button>
              </div>
            </div>
          </div>
        )}

        {isLoading && (
          <div className="fixed inset-0 bg-white/80 flex items-center justify-center z-50" role="status" aria-live="polite">
            <div className="w-10 h-10 border-4 border-stanford-red border-t-transparent rounded-full animate-spin"></div>
            <span className="sr-only">Loading...</span>
          </div>
        )}

        {/* Questions about the claim - for its claimant, the ones an admin has
            put to them. The embedded admin route (AdminPerDiemEdit) shows its
            own above the form. */}
        {!isEmbedded && claimId && (
          <ClaimQuestionsBanner claimType="PerDiemClaim" claimId={claimId} />
        )}

        {!isAdminMode && (
          <ClaimStatusBanner claimStatus={claimStatus} isNewClaim={isCreateRoute && !claimId} />
        )}

        {/* No admin feedback banner once the claim is approved (any role). */}
        {claimStatus !== 'approved' && (
          <AdminFeedbackSection
            claimStatus={claimStatus}
            adminComments={adminComments}
            processedAt={processedAt}
            processedBy={processedBy}
          />
        )}

        {/* One banner for one state. An approved claim used to show an amber
            "cannot be edited" warning directly above a green "approved"
            card - two colours contradicting each other about the same fact.
            The lock is a consequence of approval, so it is said here. */}
        {claimStatus === 'approved' && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 max-sm:p-4 mb-6 max-sm:mb-4">
            <h3 className="text-lg font-semibold text-green-800 mb-2">Approved Claim</h3>
            <p className="text-green-700 mb-4">
              {isAdminMode || isClaimReviewer
                ? 'This claim is approved, so its details are locked. Download a formatted PDF copy below.'
                : 'Your claim has been approved. Download a formatted PDF copy for your records.'}
            </p>
            <button
              onClick={downloadPDF}
              className="w-full sm:w-auto px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              aria-label="Download PDF of approved claim"
            >
              Download Claim PDF
            </button>
          </div>
        )}

        {formErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6" role="alert">
            <h3 className="text-lg font-semibold text-red-800 mb-2">Please fix the following errors:</h3>
            <ul className="list-disc list-inside text-red-700 space-y-1">
              {formErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <TravelerInfoSection
          user={user}
          lastName={lastName}
          setLastName={setLastName}
          firstName={firstName}
          setFirstName={setFirstName}
          position={position}
          setPosition={setPosition}
          businessPurpose={businessPurpose}
          setBusinessPurpose={setBusinessPurpose}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          isReadOnly={effectiveIsReadOnly}
          formatDateForInput={formatDateForInput}
          parseInputDate={parseInputDate}
          claimData={claimData}
          adminMode={isAdminMode}
          onBehalfOf={onBehalfOf}
        />

        <QuickFillSection
          quickFillStartDate={quickFillStartDate}
          setQuickFillStartDate={setQuickFillStartDate}
          quickFillEndDate={quickFillEndDate}
          setQuickFillEndDate={setQuickFillEndDate}
          quickFillCity={quickFillCity}
          quickFillCountry={quickFillCountry}
          activeSearchId={activeSearchId}
          citySearch={citySearch}
          countrySearch={countrySearch}
          handleQuickFillCityInputChange={handleQuickFillCityInputChange}
          handleQuickFillCountryInputChange={handleQuickFillCountryInputChange}
          positionDropdown={positionDropdown}
          setActiveSearchId={setActiveSearchId}
          setCitySearch={setCitySearch}
          setCountrySearch={setCountrySearch}
          setQuickFillCity={setQuickFillCity}
          setQuickFillCountry={setQuickFillCountry}
          handleQuickFill={handleQuickFill}
          exclusions={exclusions}
          onExclusionChange={(key, checked) => {
            setExclusions(prev => ({ ...prev, [key]: checked }));
            // Apply to rows that already exist; Quick Fill reads this state for new ones.
            if (key === 'lodging') handleNoLodgingAllToggle(checked);
            else handleMealExclusionToggle(key, checked);
          }}
          quickFillSegments={quickFillSegments}
          onAddQuickFillSegment={handleAddQuickFillSegment}
          onRemoveQuickFillSegment={handleRemoveQuickFillSegment}
          isReadOnly={effectiveIsReadOnly}
          claimStatus={claimStatus}
          startDate={startDate}
          endDate={endDate}
          formatDateForInput={formatDateForInput}
          isQuickFillLoading={isQuickFillLoading}
        />
         {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6" role="alert" aria-live="polite">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
          {/* Laptop-only frozen header: a bounded, scrollable area keeps the column
              headers pinned while scrolling through many days (e.g. 30 entries).
              On smaller screens the table flows normally in the page. */}
          {/* Below lg the rows are labelled cards (see .line-item-card in
              index.css), so the column header is desktop-only. */}
          <div className="lg:overflow-x-auto lg:max-h-[65vh] lg:overflow-y-auto">
          <div className={`hidden lg:grid ${claimStatus !== 'approved' ? 'lg:grid-cols-[36px_120px_minmax(140px,1fr)_80px_70px_70px_70px_80px_70px_70px_90px_minmax(100px,1fr)_90px]' : 'lg:grid-cols-[36px_120px_minmax(160px,1fr)_80px_70px_70px_70px_80px_70px_70px_90px_minmax(120px,1fr)]'} gap-2 p-3 bg-stanford-red text-white text-xs font-semibold min-w-[1100px] lg:sticky lg:top-0 z-20`} role="row">
            <div className="flex items-center justify-center" role="columnheader">
              <input
                type="checkbox"
                checked={sortedLineItems.length > 0 && selectedLineIds.size === sortedLineItems.length}
                onChange={(e) => setSelectedLineIds(
                  e.target.checked ? new Set(sortedLineItems.map(i => i.id)) : new Set()
                )}
                disabled={effectiveIsReadOnly}
                className="w-4 h-4 rounded border-gray-300"
                aria-label="Select all lines"
              />
            </div>
            <div role="columnheader">Date(s) Claimed</div>
            <div role="columnheader">City, Country*</div>
            <div className="text-center" role="columnheader">State Dept M&IE</div>
            <div className="text-center" role="columnheader">Breakfast**</div>
            <div className="text-center" role="columnheader">Lunch**</div>
            <div className="text-center" role="columnheader">Dinner**</div>
            <div className="text-center" role="columnheader">Incidentals**</div>
            <div className="text-center" role="columnheader">Total M&IE</div>
            <div className="text-center" role="columnheader">Lodging</div>
            <div className="text-center" role="columnheader">Running Total</div>
            <div role="columnheader">Comments</div>
            {claimStatus !== 'approved' && (
              <div role="columnheader">Actions</div>
            )}
          </div>

          {/* Phones: the select-all box lives in the hidden header, so repeat it. */}
          {sortedLineItems.length > 0 && (
            <label className="lg:hidden flex items-center gap-3 px-4 py-3 bg-stanford-red text-white text-sm font-semibold cursor-pointer">
              <input
                type="checkbox"
                checked={sortedLineItems.length > 0 && selectedLineIds.size === sortedLineItems.length}
                onChange={(e) => setSelectedLineIds(
                  e.target.checked ? new Set(sortedLineItems.map(i => i.id)) : new Set()
                )}
                disabled={effectiveIsReadOnly}
                className="w-5 h-5 rounded border-gray-300"
                aria-label="Select all lines"
              />
              {sortedLineItems.length} day{sortedLineItems.length === 1 ? '' : 's'} claimed
            </label>
          )}

          {/* Bulk actions for the ticked lines. */}
          {selectedLineIds.size > 0 && !effectiveIsReadOnly && (
            <div className="mx-2 my-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-blue-800">
                {selectedLineIds.size} line{selectedLineIds.size > 1 ? 's' : ''} selected
              </span>
              <button
                onClick={async () => {
                  if (!await confirm(`Delete ${selectedLineIds.size} selected line(s)?`)) return;
                  deleteLineItems([...selectedLineIds]);
                  setSelectedLineIds(new Set());
                }}
                className="min-h-[40px] px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Selected
              </button>
              <button
                onClick={() => {
                  restoreLineItems([...selectedLineIds]);
                  setSelectedLineIds(new Set());
                }}
                className="min-h-[40px] px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                title="Restore the original State Department rates on these lines"
              >
                Restore Selected
              </button>
              <button
                onClick={() => setSelectedLineIds(new Set())}
                className="min-h-[40px] px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Clear Selection
              </button>
            </div>
          )}

          {/* Surface duplicate days up front - the per-row markers are easy to
              miss on a long claim, and this used to only be caught at submit. */}
          {outsideTripLabels.length > 0 && (
            <div className="mx-2 my-2 bg-red-50 border border-red-300 text-red-800 px-4 py-2 rounded-lg text-sm">
              <span className="font-semibold">Outside the travel dates:</span>{' '}
              {outsideTripLabels.join(', ')} - every line must be a day of the trip. Change the line's date,
              remove it, or adjust the travel dates before submitting.
            </div>
          )}

          {duplicateDayLabels.length > 0 && (
            <div className="mx-2 my-2 bg-amber-50 border border-amber-300 text-amber-800 px-4 py-2 rounded-lg text-sm">
              <span className="font-semibold">Duplicate days:</span>{' '}
              {duplicateDayLabels.join(', ')} - each day should appear once. Remove or merge the extra lines before submitting.
            </div>
          )}

          {/* FIXED: Use memoized sorted line items */}
          {sortedLineItems.map((item) => (
            <ExpenseTableRow
              key={item.id}
              item={item}
              isSelected={selectedLineIds.has(item.id)}
              onToggleSelect={(id) => setSelectedLineIds(prev => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id); else next.add(id);
                return next;
              })}
              updateLineItem={updateLineItem}
              deleteLineItem={deleteLineItem}
              restoreLineItem={restoreLineItem}
              apply75Percent={apply75Percent}
              toggleAdminComment={toggleAdminComment}
              saveAdminComment={saveAdminComment}
              appliedRates={appliedRates}
              activeSearchId={activeSearchId}
              citySearch={citySearch}
              handleCityInputChange={handleCityInputChange}
              handleCityInputFocus={handleCityInputFocus}
              handleKeyDown={handleKeyDown}
              formatDateForInput={formatDateForInput}
              parseInputDate={parseInputDate}
              inputRefs={inputRefs}
              adminMode={isAdminMode}
              lineItems={sortedLineItems}
              isReadOnly={effectiveIsReadOnly}
              claimStatus={claimStatus}
              startDate={startDate}
              endDate={endDate}
            />
          ))}
          </div>

          {!effectiveIsReadOnly && claimStatus !== 'approved' && (
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={handleAddLineItem}
                className="w-full sm:w-auto min-h-[44px] px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors"
                aria-label="Add new line item"
              >
                + Add Line Item
              </button>
            </div>
          )}

          <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
            <div className="flex items-baseline justify-between sm:justify-end w-full sm:w-auto gap-4">
              <span className="text-base sm:text-lg font-semibold text-gray-800">Total Amount:</span>
              <span className="text-xl sm:text-2xl font-bold text-stanford-red tabular-nums" aria-label={`Total amount: $${(Number(totalAmount) || 0).toFixed(2)}`}>
                ${(Number(totalAmount) || 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6" role="alert" aria-live="polite">
            {error}
          </div>
        )}

        {/* Anyone who reviews or processes this claim can take it into a
            spreadsheet, not just the admin who edits it. */}
        {canDownloadClaimExcel && (
          <div className="mb-4">
            <button
              onClick={async () => {
                try {
                  await exportPerDiemClaimToExcel({
                    ...(claimData || {}),
                    id: claimId,
                    startDate,
                    endDate,
                    businessPurpose,
                    lineItems,
                  }, ptaeAllocation);
                } catch (err) {
                  alert('Could not export to Excel: ' + err.message);
                }
              }}
              className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 transition-colors"
            >
              Download Excel
            </button>
          </div>
        )}

        {/* Approval + rejection history. The hash chain was already returned on
            every claim fetch but rendered nowhere, so a claim returned twice
            looked identical to one submitted cleanly and the reason it came back
            vanished as soon as it was resubmitted. */}
        {claimId && (
          <ClaimHistoryPanel
            approvalChain={claimData?.approvalChain}
            editHistory={claimData?.editHistory}
            defaultOpen={claimStatus === 'returned' || claimStatus === 'rejected'}
          />
        )}

        {/* PTAE allocation - the admin approves from this form, so funding must be
            assignable here (approval is blocked without it).

            claimTotal is the LIVE total, not the saved one. It used to read
            `claimData?.totalClaim ?? totalAmount`, which never reached
            totalAmount - ?? only falls through on null/undefined and the saved
            value is always set once the claim loads. So excluding a meal moved
            the displayed total but not the figure the split was measured
            against, and an unbalanced split looked balanced right up until
            approval refused it. Falls back to the saved total only while there
            are no rows to sum. */}
        {showPtaeAllocation && (
          <ClaimPtaeAllocation
            claimType="perdiem"
            claimId={claimId}
            claimTotal={lineItems?.length ? totalAmount : (claimData?.totalClaim ?? 0)}
            // A reviewer never edits funding, whatever the claim's status.
            readOnly={!isAdminMode || !['submitted', 'returned', 'under_review'].includes(claimStatus)}
            onDirtyChange={setPtaeAllocationDirty}
            onAllocationLoaded={handleAllocationLoaded}
            liveLineValues={isAdminMode ? liveLineValues : null}
            // Reload once the claim is saved: the server re-prices the days and
            // resizes the buckets to match.
            refreshKey={saveSuccess}
            // Assign PTAE saves first when days were added or removed.
            onSaveClaim={isAdminMode && !effectiveIsReadOnly ? saveAdminChanges : undefined}
          />
        )}

        {!effectiveIsReadOnly && claimStatus !== 'approved' && !isAdminMode && (
          <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap gap-3 sm:gap-4 mb-6">
            {!isAdminMode && (
              <>
                {!onBehalfOf && (
                  <button
                    onClick={() => saveForm(true, false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    disabled={isLoading || effectiveIsReadOnly}
                    aria-label="Save as draft"
                  >
                    Save as Draft
                  </button>
                )}

                {/* Only once a draft exists, and only while it is still a draft -
                    a submitted claim must not be deletable from here. */}
                {(claimId || savedDraftId) && claimStatus === 'draft' && (
                  <button
                    onClick={async () => {
                      if (!await confirm('Delete this draft claim? This cannot be undone.')) return;
                      await deletePerDiemClaim();
                    }}
                    className="px-6 py-3 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                    disabled={isLoading}
                    aria-label="Delete draft"
                  >
                    Delete Draft
                  </button>
                )}
                <button
                  onClick={() => saveForm(false, false)}
                  className="px-6 py-3 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50"
                  disabled={isLoading || effectiveIsReadOnly}
                  aria-label="Submit claim"
                >
                  {onBehalfOf ? `File and submit for ${onBehalfOf.firstName}` : 'Submit'}
                </button>
              </>
            )}
          </div>
        )}

        {/* An approved claim is final. Send-back was previously offered here, but
            reversing an approval mid-workflow left the claim and any report it
            had been rolled into disagreeing about the amount. */}

        {/* FIXED: Only show DocumentManagement for admins */}
        {isAdminMode && (
          <DocumentManagement
            claimId={claimId}
            supportingDocuments={supportingDocumentsDisplay}
            uploadSupportingDocuments={uploadSupportingDocuments}
            deleteSupportingDocument={deleteSupportingDocument}
            isLoading={isLoading}
            canDelete={canDeleteDocuments}
            noticeAudience="reviewer"
          />
        )}

        {/* dropdown div*/}
        {cityResults && cityResults.length > 0 && activeSearchId && claimStatus !== 'approved' && portalElement &&
          ReactDOM.createPortal(
            <div
              className="city-search-dropdown"
              style={{
                position: 'fixed',
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                backgroundColor: 'white',
                borderRadius: '4px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                maxHeight: '200px',
                overflowY: 'auto',
                zIndex: 999999,
                border: '1px solid #ddd',
                pointerEvents: 'auto'  // Enable clicks on dropdown (portal has pointerEvents: none)
              }}
            >
              <ul style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
              }}>

              {cityResults.map((city, index) => (
                <li
                  key={city.id || index}
                  onMouseDown={(e) => {
                    // Handle selection on mousedown instead of click
                    // This ensures selection happens before any blur/state changes can remove the dropdown
                    e.preventDefault();
                    e.stopPropagation();

                    if (activeSearchId === 'quickFillCountry') {
                      const countryName = city.country || city.name || city.display;
                      handleQuickFillCountrySelect(countryName);
                    } else if (activeSearchId === 'quickFillCity') {
                      if (city.isOther) {
                        handleQuickFillCitySelect('Other', city.country);
                      } else {
                        handleQuickFillCitySelect(city.name, city.country);
                      }
                    } else {
                      if (city.isOther) {
                        handleCitySelect(activeSearchId, 'Other', city.country);
                      } else {
                        handleCitySelect(activeSearchId, city.name, city.country);
                      }
                    }
                  }}
                    style={{
                      padding: '12px',
                      cursor: 'pointer',
                      borderBottom: index < cityResults.length - 1 ? '1px solid #eee' : 'none',
                      backgroundColor: city.isOther ? '#fff3cd' : 'white',
                      color: 'black',
                      fontSize: '14px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = city.isOther ? '#ffe69c' : '#f0f0f0';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = city.isOther ? '#fff3cd' : 'white';
                    }}
                  >
                    {city.isOther && '* '}
                    {activeSearchId === 'quickFillCountry'
                      ? city.display || city.country
                      : city.isOther
                        ? `${city.display || city.customInput || city.name}, ${city.country}`
                        : `${city.name}, ${city.country}`
                    }
                    {city.isOther && ' (Custom Entry)'}
                  </li>
                ))}
              </ul>
            </div>,
            portalElement
          )
        }

        <div className="bg-white rounded-xl shadow-lg p-6 max-sm:p-4 mt-4 sm:mt-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">Claim Details</h2>

          <ClaimMetadata
            createdAt={claimData?.createdAt}
            approvedAt={claimData?.processedAt}
            approvedBy={claimData?.processedBy}
          />

          {/* FIXED: Show supporting documents only once and only for non-admin users */}
          {!isAdminMode && supportingDocumentsDisplay && supportingDocumentsDisplay.length > 0 && (
            <div className="mt-4 sm:mt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Supporting Documents</h3>
              <div className="space-y-4">
                {supportingDocumentsDisplay.map((doc, idx) => (
                  <PdfSupportingViewer
                    key={`${doc.id || doc.fileName}-${idx}`} // FIXED: Better key
                    infected={isInfected(doc)}
                    pdfUrl={doc.fileUrl}
                    description={doc.description}
                    fileName={doc.fileName}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* The admin's decision sits under everything it decides on - the days,
            the funding, the documents - rather than above the documents, where
            it invited approving before they had been looked at. */}
        {isAdminMode && !effectiveIsReadOnly && claimStatus !== 'approved' && (
          <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap gap-3 sm:gap-4 mt-4 sm:mt-6 mb-6">
            <button
              onClick={async () => {
                try {
                  await saveAdminChanges();
                } catch (_e) { /* error already set in hook */ }
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={isLoading || isSaving || ptaeAllocationDirty}
              title={ptaeAllocationDirty
                ? 'Save the PTA allocation first - saving the claim now would lose your allocation changes'
                : ''}
            >
              {isSaving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Changes'}
            </button>
            <button
              onClick={async () => {
                const id = claimId || claimData?.claimId;
                if (!id) return;
                if (await confirm('Discard your unsaved changes and restore this claim to its last-saved version?')) {
                  loadClaimData(id);
                }
              }}
              className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
              disabled={isLoading || isSaving}
            >
              Revert
            </button>
            <button
              onClick={handleAdminAction}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              disabled={isLoading || isSaving}
            >
              Take Administrative Action
            </button>
          </div>
        )}

        {showActionModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-0" onClick={() => setShowActionModal(false)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full sm:mx-4 p-6 max-sm:p-5 flex flex-col max-h-[calc(100dvh-1.5rem)]" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4 shrink-0">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Administrative Decision</h3>
                <button
                  className="p-2 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 -mr-2 sm:mr-0 text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={() => setShowActionModal(false)}
                  aria-label="Close"
                >
                  X
                </button>
              </div>

              <div className="space-y-4 min-h-0 overflow-y-auto -m-1 p-1">
                {claimStatus !== 'approved' && (
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="adminAction"
                        value="approve"
                        checked={selectedAction === 'approve'}
                        onChange={(e) => setSelectedAction(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <strong className="text-green-600">Yes Approve Claim</strong>
                        <p className="text-sm text-gray-500">Claim meets all requirements</p>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="adminAction"
                        value="reject"
                        checked={selectedAction === 'reject'}
                        onChange={(e) => setSelectedAction(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <strong className="text-red-600">No Reject Claim</strong>
                        <p className="text-sm text-gray-500">Claim has issues that cannot be resolved</p>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="adminAction"
                        value="sendBack"
                        checked={selectedAction === 'sendBack'}
                        onChange={(e) => setSelectedAction(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <strong className="text-orange-600"> Send Back for Revision</strong>
                        <p className="text-sm text-gray-500">Needs changes before approval</p>
                      </div>
                    </label>
                  </div>
                )}

                {claimStatus === 'approved' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="font-semibold text-amber-800">Withdraw approval</p>
                    <p className="text-sm text-amber-700 mt-1">
                      This claim is approved and waiting for the auditor. Withdrawing returns it to
                      Under Review so you can correct it and approve again. Once the auditor has
                      audited it, or it has gone into a report under review, this is no longer possible.
                    </p>
                    <button
                      onClick={withdrawApproval}
                      disabled={isLoading}
                      className="mt-3 w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                    >
                      {isLoading ? 'Withdrawing…' : 'Withdraw approval'}
                    </button>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Comments {(selectedAction === 'reject' || selectedAction === 'sendBack') && <span className="text-red-500">*</span>}
                  </label>
                  <textarea
                    value={adminDecisionComment}
                    onChange={(e) => setAdminDecisionComment(e.target.value)}
                    placeholder="Provide details about your decision..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                    rows="4"
                    required={selectedAction === 'reject' || selectedAction === 'sendBack'}
                  />
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-3 mt-5 sm:mt-6 shrink-0">
                <button
                  onClick={() => setShowActionModal(false)}
                  className="flex-1 min-h-[44px] sm:min-h-0 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleModalSubmit}
                  className="flex-1 min-h-[44px] sm:min-h-0 px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!selectedAction || isLoading || claimStatus === 'approved'}
                >
                  Submit Decision
                </button>
              </div>
            </div>
          </div>
        )}
    </Frame>
  );
};

PerDiemForm.propTypes = {
  adminMode: PropTypes.bool
};

PerDiemForm.defaultProps = {
  adminMode: false
};

export default PerDiemForm;
